import React, { useState, useMemo, useEffect } from 'react';
import ReactMapGL, { useControl } from 'react-map-gl';
import maplibregl from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import { MapboxOverlay } from '@deck.gl/mapbox';
import { WebMercatorViewport } from '@deck.gl/core';
import StopToolTip from './StopToolTip';
import { renderPath } from './helper';

function DeckGLOverlay(props) {
  const overlay = useControl(() => new MapboxOverlay(props));
  overlay.setProps(props);
  return null;
}

const DEFAULT_VIEWPORT_WIDTH = 800;
const DEFAULT_VIEWPORT_HEIGHT = 600;

const MapCanvas = (props) => {
  const { stops, assignment } = props;

  const [hoverStop, setHoverStop] = useState(null);
  const renderResult = useMemo(() => {
    if (!stops) return [];
    return renderPath(stops, setHoverStop);
  }, [stops]);

  const [viewport, setViewport] = useState({
    width: 400,
    height: 300,
    latitude: 37.7577,
    longitude: -122.4376,
    zoom: 11,
    transitionDuration: 50,
  });

  useEffect(() => {
    if (!renderResult) return;
    if (!assignment?.bbox) return;
    if (!Array.isArray(assignment.bbox) || assignment.bbox.length < 2) return;
    if (!assignment.bbox[0] || !assignment.bbox[1]) return;
    
    // Validate bbox data
    const minLng = assignment.bbox[0][0];
    const maxLng = assignment.bbox[1][0];
    const minLat = assignment.bbox[0][1];
    const maxLat = assignment.bbox[1][1];

    if (!Number.isFinite(minLng) || !Number.isFinite(maxLng) ||
        !Number.isFinite(minLat) || !Number.isFinite(maxLat)) return;

    // Ensure viewport has width and height for fitBounds
    const viewportWithDimensions = {
      ...viewport,
      width: viewport.width || window.innerWidth || DEFAULT_VIEWPORT_WIDTH,
      height: viewport.height || window.innerHeight || DEFAULT_VIEWPORT_HEIGHT,
    };
    const viewportWeb = new WebMercatorViewport(viewportWithDimensions);

    const vw = maxLng - minLng;
    const vh = maxLat - minLat;
    const gapw = Math.max(vw * 0.1, 0.0004);
    const gaph = Math.max(vh * 0.1, 0.0004);
    const bbox = [
      [minLng - gapw, minLat - gaph],
      [maxLng + gapw, maxLat + gaph],
    ];

    let { longitude, latitude, zoom } = viewportWeb.fitBounds(bbox, { padding: 50 });
    setViewport({ ...viewport, longitude, latitude, zoom, transitionDuration: 50 });
  }, [renderResult, assignment]);
  return (
    <ReactMapGL
      {...viewport}
      mapStyle={`https://api.maptiler.com/maps/${import.meta.env.REACT_APP_MAP_TILER_STYLE}/style.json?key=${import.meta.env.REACT_APP_MAP_TILER_KEY}`}
      mapLib={maplibregl}
      onMove={(evt) => setViewport(evt.viewState)}
      style={{ width: '100%', height: '100%' }}
    >
      <DeckGLOverlay layers={[...renderResult]} />
      <StopToolTip hoverStop={hoverStop} assignmentTimezone={assignment?.assignment?.timezone} onClose={() => setHoverStop(null)} />
    </ReactMapGL>
  );
};

export default MapCanvas;
